import { useContext } from 'react'
import { RosterSetupContext } from '../RosterSetup'
import { cn } from '@/lib/utils'
import Card from 'app/frontend/base/components/Card'
import { useTeam } from 'app/frontend/base/config/context/team'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

type Props<T> = {
  title: string
  subject: Record<string, T>
  memberFilterFn: (member: wowaudit.TeamMembership, matchEntities: T) => boolean
  valueFn: (type: string, matchEntities: T) => React.ReactNode
  className?: string
}

const StatTable = <T,>({
  title,
  subject,
  memberFilterFn,
  valueFn,
  className = '',
}: Props<T>) => {
  const { team } = useTeam()
  const { mains, alts, reserves } = useContext(RosterSetupContext)
  const hasReserveRanks = team.teamRanks?.some(
    (rank) => rank.player_type === 'reserve',
  )
  const displayMains = mains.filter(
    (member) => member.teamRank.player_type === 'main',
  )

  return (
    <Card className={cn('w-full', className)} size='sm'>
      <table className='w-full'>
        <thead>
          <tr className='[&>th]:px-2 [&>th:first-child]:pl-0 [&>th]:font-normal [&>th]:text-xs'>
            <th className='text-left'>{title}</th>
            <th>Main</th>
            {hasReserveRanks ? (
              <th>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>Res</span>
                  </TooltipTrigger>
                  <TooltipContent>Reserves</TooltipContent>
                </Tooltip>
              </th>
            ) : null}
            <th>Alt</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(subject).map(([type, matchEntities]) => {
            const mainCount = displayMains.filter((member) =>
              memberFilterFn(member, matchEntities),
            ).length
            const reserveCount = reserves.filter((member) =>
              memberFilterFn(member, matchEntities),
            ).length
            const altCount = alts.filter((member) =>
              memberFilterFn(member, matchEntities),
            ).length

            return (
              <tr key={type} className='border-t'>
                <td>
                  <div
                    className={`flex items-center gap-1.5 mr-2 whitespace-nowrap ${
                      mainCount > 0 ? '' : '[&_span]:opacity-60 [&_span]:italic'
                    }`}
                  >
                    {valueFn(type, matchEntities)}
                  </div>
                </td>
                <td className='text-center' width='10%'>
                  {mainCount || <span className='opacity-30'>-</span>}
                </td>
                {hasReserveRanks ? (
                  <td className='text-center' width='10%'>
                    {reserveCount || <span className='opacity-30'>-</span>}
                  </td>
                ) : null}
                <td className='text-center' width='10%'>
                  {altCount || <span className='opacity-30'>-</span>}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </Card>
  )
}

export default StatTable
